import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { Spinner } from "@/components/ui/spinner";
import MerchantContext from "@/contexts/MerchantContext";
import UserContext from "@/contexts/UserContext";
import { usePipeline, usePipelinePositions } from "@/contexts/PipelineContext";
import { isDataScopedEmployee } from "@/utils/permissions";
import { hasTickets } from "@/utils/pipelines";
import posthog from "@/lib/posthog";
import { trpc } from "@/trpc";
import type { TimelineItem } from "@/components/leads/chat/ConversationTimeline";
import MessageComposer from "@/components/leads/chat/composer/MessageComposer";
import ChatErrorFallback from "@/components/leads/chat/ChatErrorFallback";
import ErrorBoundary from "@/components/ErrorBoundary";
import { useOptimisticMessages } from "@/components/leads/chat/composer/useOptimisticMessages";
import { cn } from "@/lib/utils";
import { STAGE_ORDER } from "@/components/tablero/types";
import type { TicketPosition } from "@/components/tablero/solicitudes/types";
import AsesorControl from "./AsesorControl";
import ChatTimeline from "./ChatTimeline";
import PerfilPanel from "./PerfilPanel";
import { conversationPanelPositions } from "./positions";

type Props = {
  leadId?: string;
  // Which surface is hosting the panel. Drives the posthog `surface`/`via` tags,
  // the composer's own tagging, and how PerfilPanel builds its shareable link.
  surface: "inbox" | "tablero";
  // True when there is room to show the perfil beside the conversation; false puts
  // it behind a "Perfil" button in the header instead. Same PerfilPanel either way.
  profileInline: boolean;
  // False while the host is closed (a drawer), so the per-lead queries below stay
  // idle instead of fetching for a lead nobody is looking at. Surfaces that mount
  // the panel inline leave it at the default.
  isActive?: boolean;
  // Rendered as a back arrow in the header. The inbox passes it on mobile, where
  // the list and the conversation share the screen one at a time.
  onBack?: () => void;
};

const LeadConversationPanel = ({
  leadId,
  surface,
  profileInline,
  isActive = true,
  onBack,
}: Props) => {
  const [isProfileSheetOpen, setIsProfileSheetOpen] = useState(false);
  const { merchantId } = useContext(MerchantContext);
  const { roles, employeeIds } = useContext(UserContext);
  const { allPositions } = usePipeline();
  const utils = trpc.useUtils();

  const leadKey = { merchantId, id: leadId || "" };
  const { data: lead, isLoading: isLoadingLead } = trpc.leads.get.useQuery(leadKey, {
    enabled: !!merchantId && !!leadId && isActive,
  });

  // A data-scoped employee may only open leads assigned to them; a foreign id
  // can only arrive via a ?lead= deep link (the board never shows other
  // employees' leads). The lead must resolve first to know its owner, so the
  // dependent queries below wait on this instead of fetching in parallel.
  const isDataScoped = isDataScopedEmployee(roles);
  const isOwnLead = !!lead?.employeeId && employeeIds.includes(lead.employeeId);
  const isForeignLead = isDataScoped && !!lead && !isOwnLead;
  const canViewLead = !isDataScoped || isOwnLead;

  // Etapa options and ticket statuses come from the LEAD's own pipeline, not from
  // whatever pipeline the host surface happens to have selected. The inbox is
  // cross-pipeline and has no selection to hand down; on the tablero the two are
  // the same pipeline in every case except a ?lead= deep link into another board,
  // which used to render that lead against the wrong pipeline's positions.
  // Falling back to "default" mirrors what the backend does for a lead written
  // before pipelineId was required (leads.ts `lead.pipelineId || DEFAULT_PIPELINE_ID`);
  // without it such a lead would resolve to no positions and a dead etapa selector.
  const leadPipelineId = lead ? (lead.pipelineId ?? "default") : undefined;
  const allLeadPositions = usePipelinePositions(leadPipelineId);
  const positions = useMemo(
    () => conversationPanelPositions(allLeadPositions),
    [allLeadPositions]
  );

  // Ticket statuses are per-pipeline and merchant-configurable, and are a
  // different set from the lead positions above. Only used to name the lead's
  // solicitudes; on a pipeline without tickets this stays empty and the section
  // falls back to raw status ids.
  const { data: ticketPositionsByStage } = trpc.pipeline.getTicketPositions.useQuery(
    { merchantId, pipelineId: leadPipelineId ?? "" },
    {
      enabled: !!merchantId && !!leadPipelineId && hasTickets(leadPipelineId ?? ""),
      staleTime: 5 * 60 * 1000,
    }
  );
  // Flattened into one rail in stage order — a pipeline may legitimately lack a
  // whole stage, so stages with no positions simply contribute nothing.
  const ticketPositions = useMemo<TicketPosition[]>(() => {
    if (!ticketPositionsByStage) return [];
    const byStage = ticketPositionsByStage as Record<string, TicketPosition[]>;
    return STAGE_ORDER.flatMap((stage) =>
      [...(byStage[stage] ?? [])].sort((a, b) => a.order - b.order)
    );
  }, [ticketPositionsByStage]);

  const {
    data: timelineData,
    isLoading: isLoadingTimeline,
    refetch: refetchTimeline,
  } = trpc.conversations.listConversationTimeline.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: conversationData, refetch: refetchConversation } = trpc.conversations.get.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: appointmentsData } = trpc.appointments.list.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: crmSyncLogs } = trpc.leads.listCrmSyncLogs.useQuery(
    { merchantId, id: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: solicitudesData } = trpc.serviceRequests.listByLead.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: searchedCodes } = trpc.leads.listSearchedCodes.useQuery(
    { merchantId, id: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  // Captación requests and voice calls: both come back empty for merchants without the
  // product, and PerfilPanel drops the whole section when they do. Shared query keys, so
  // opening a lead from either surface warms the other's cache.
  const { data: listingRequestsData } = trpc.listingRequest.list.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: callsData } = trpc.calls.list.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const { data: notesData } = trpc.leadNotes.list.useQuery(
    { merchantId, leadId: leadId || "" },
    { enabled: !!merchantId && !!leadId && isActive && canViewLead }
  );

  const optimistic = useOptimisticMessages();
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const timelineItems = useMemo(() => {
    const baseItems = (timelineData as TimelineItem[] | undefined) ?? [];
    return optimistic.mergeWithTimeline(baseItems);
  }, [timelineData, optimistic.mergeWithTimeline]);

  // Both panel lists read chronologically: oldest first, most recent last.
  // The timeline already arrives ascending; CRM sync logs arrive newest-first.
  const timelineActivities = timelineItems
    .filter((item): item is Extract<TimelineItem, { type: "activity" }> => item.type === "activity")
    .map((item) => item.activity);
  const crmSyncLogsChronological = [...(crmSyncLogs ?? [])].reverse();

  // Scroll to bottom when messages change (sends and live inbound updates).
  useEffect(() => {
    scrollContainerRef.current?.scrollTo({
      top: scrollContainerRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [timelineItems]);

  // After a send lands, refresh the timeline and let both surfaces pick up the
  // lastLeadMessageAt reorder without waiting for the websocket. Each surface's
  // queries are a no-op for whichever one is not mounted.
  const refetchTimelineAndBoard = useCallback(async () => {
    await refetchTimeline();
    utils.leads.listActiveByPosition.invalidate();
    utils.leads.searchActiveNonRealtime.invalidate();
    // Same predicate WebSocketContext uses: only the live top page. Deeper pages
    // are Tinybird-backed, and a search result set is keyed on handles, which a
    // new message does not change.
    utils.inbox.list.invalidate(undefined, {
      predicate: (query) => {
        const input = (
          query.queryKey[1] as { input?: { page?: number; search?: string } } | undefined
        )?.input;
        return input?.page === 0 && !input?.search;
      },
    });
  }, [refetchTimeline, utils]);

  const moveMutation = trpc.leads.movePipelinePosition.useMutation();
  const retryMessageMutation = trpc.conversations.retryMessage.useMutation();

  const handleRetryMessage = useCallback(
    (messageId: string) => {
      if (!leadId) return;
      retryMessageMutation.mutate(
        { merchantId, leadId, messageId },
        {
          onSuccess: (result) => {
            refetchTimeline();
            if (!result.success) {
              toast.warning("Error al reenviar mensaje", {
                description: "El mensaje no se pudo enviar a WhatsApp. Puedes reintentar.",
                duration: 5000,
              });
            }
          },
          onError: () => {
            refetchTimeline();
            toast.error("Error al reenviar mensaje", {
              description: "No se pudo reenviar el mensaje. Por favor, intenta de nuevo.",
              duration: 5000,
            });
          },
        }
      );
    },
    [merchantId, leadId, retryMessageMutation, refetchTimeline]
  );

  const handleChangeEtapa = async (positionId: string) => {
    if (!lead || positionId === lead.pipelinePositionId) return;
    const prev = utils.leads.get.getData(leadKey);
    const target = positions.find((p) => p.id === positionId);

    // Optimistic panel update; the board refetches via invalidate.
    utils.leads.get.setData(leadKey, {
      ...lead,
      pipelinePositionId: positionId,
      pipelinePositionDisplayName: target?.displayName ?? positionId,
      pipelinePositionStage: target?.stage,
    });

    try {
      await moveMutation.mutateAsync({
        merchantId,
        leadId: lead.id,
        pipelinePositionId: positionId,
      });
      utils.leads.listActiveByPosition.invalidate();
      utils.leads.searchActiveNonRealtime.invalidate();
      toast.success(`Lead movido a ${target?.displayName ?? positionId}`);
      posthog.capture("tablero lead moved", {
        merchant_id: merchantId,
        lead_id: lead.id,
        source_position_id: lead.pipelinePositionId,
        target_position_id: positionId,
        target_position_name: target?.displayName,
        via: surface,
      });
    } catch (error) {
      if (prev) utils.leads.get.setData(leadKey, prev);
      toast.error("No pudimos mover el lead. Intenta de nuevo.");
      console.error("movePipelinePosition failed", error);
    }
  };

  if (isLoadingLead || !lead) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size="xl" />
      </div>
    );
  }

  if (isForeignLead) {
    return (
      <div className="flex h-full items-center justify-center px-6 text-center text-sm text-neutral-600">
        No tienes acceso a este lead.
      </div>
    );
  }

  const perfil = (
    <PerfilPanel
      lead={lead}
      positions={positions}
      onChangeEtapa={handleChangeEtapa}
      isMoving={moveMutation.isLoading}
      appointments={appointmentsData?.data ?? []}
      activities={timelineActivities}
      crmSyncLogs={crmSyncLogsChronological}
      allPositions={allPositions}
      solicitudes={solicitudesData?.data ?? []}
      ticketPositions={ticketPositions}
      searchedCodes={searchedCodes ?? []}
      listingRequests={listingRequestsData?.data ?? []}
      calls={callsData ?? []}
      notes={notesData}
      merchantId={merchantId}
      surface={surface}
      conversation={conversationData ?? null}
    />
  );

  return (
    <div
      className={cn(
        "flex h-full overflow-hidden",
        profileInline ? "flex-col md:flex-row" : "flex-col"
      )}
    >
      <div
        className={cn(
          "flex min-h-0 min-w-0 flex-1 flex-col",
          profileInline && "border-r border-neutral-300"
        )}
      >
        <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b border-neutral-300 bg-surface px-4">
          <div className="flex min-w-0 items-center gap-1.5">
            {onBack && (
              <button
                aria-label="Volver"
                onClick={onBack}
                className="-ml-2 flex size-8 shrink-0 items-center justify-center rounded-md hover:bg-neutral-100"
              >
                <ArrowLeft className="size-4" />
              </button>
            )}
            <span className="truncate text-sm font-semibold text-neutral-900">Conversación</span>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <AsesorControl
              merchantId={merchantId}
              lead={lead}
              hasConversation={Boolean(conversationData)}
            />
            {!profileInline && (
              <Button variant="outline" size="sm" onClick={() => setIsProfileSheetOpen(true)}>
                <Eye className="mr-1.5 size-4" />
                Perfil
              </Button>
            )}
          </div>
        </div>
        {/* Timeline + composer only — the header and the perfil panel stay
            outside so they survive a crash in the conversation itself. */}
        <ErrorBoundary
          surface="tablero-chat"
          resetKey={leadId}
          fallback={(args) => (
            <div className="min-h-0 flex-1">
              <ChatErrorFallback {...args} />
            </div>
          )}
        >
          <div ref={scrollContainerRef} className="min-h-0 flex-1 overflow-y-auto bg-neutral-100/30">
            <ChatTimeline
              items={timelineItems}
              isLoading={isLoadingTimeline}
              allPositions={allPositions}
              onRetryMessage={handleRetryMessage}
            />
          </div>
          {conversationData && leadId && (
            <MessageComposer
              merchantId={merchantId}
              leadId={leadId}
              leadData={lead}
              conversationData={conversationData}
              refetchTimeline={refetchTimelineAndBoard}
              refetchConversation={refetchConversation}
              optimistic={optimistic}
              posthogSurface={surface}
            />
          )}
        </ErrorBoundary>
      </div>
      {profileInline ? (
        <div className="min-h-0 shrink-0 md:w-[380px]">{perfil}</div>
      ) : (
        <Sheet open={isProfileSheetOpen} onOpenChange={setIsProfileSheetOpen}>
          <SheetContent side="right" className="w-full p-0 sm:max-w-none md:w-[420px]">
            <SheetTitle className="sr-only">Perfil del lead</SheetTitle>
            {perfil}
          </SheetContent>
        </Sheet>
      )}
    </div>
  );
};

export default LeadConversationPanel;
